// TODO: This file was created by bulk-decaffeinate.
// Fix any style issues and re-enable lint.
/*
 * decaffeinate suggestions:
 * DS101: Remove unnecessary use of Array.from
 * DS102: Remove unnecessary code created because of implicit returns
 * DS201: Simplify complex destructure assignments
 * DS205: Consider reworking code to avoid use of IIFEs
 * DS207: Consider shorter variations of null checks
 * Full docs: https://github.com/decaffeinate/decaffeinate/blob/master/docs/suggestions.md
 */

import {Button, Tooltip} from '@mantine/core';
import classNames from 'classnames';
import reactUpdate from 'immutability-helper';
import React, {ReactNode} from 'react';
import _ from 'underscore';

import * as analytics from 'analytics/Analytics';
import {Assembly, GibsonParameters, GoldenGateParameters} from 'assembly/AssemblyModels';
import * as util from 'biojs/Util';
import {ANALYTICS_MODULE} from 'constants/AnalyticsConstants';
import {ASSEMBLY_TYPE} from 'constants/AssemblyConstants';
import Enzymes from 'molbio/enzymes/backbone/Enzymes';
import EnzymeConstants from 'molbio/enzymes/EnzymeConstants';
import notifications from 'notifications';
import getTestProps from 'test/lib/cypress/getTestProps';
import Col from 'ui-library/bs/Col';
import Row from 'ui-library/bs/Row';
import {FloatingPlacement} from 'ui-library/floating-ui/FloatingConstants';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';
import PopoverIcon from 'ui-library/PopoverIcon';
import {InputChangeEvent, SelectChangeEvent} from 'util/Types';

const {TYPE_IIS_ENZYME_IDS} = EnzymeConstants;
const COMMON_ENZYMES = Enzymes.getCommonEnzymes();
const ASSEMBLY_NAME_MAX_LENGTH = 128; // Should match server.

type AssemblyParameters<TAssembly extends ASSEMBLY_TYPE> = TAssembly extends ASSEMBLY_TYPE.GIBSON
  ? GibsonParameters
  : TAssembly extends ASSEMBLY_TYPE.GOLDEN_GATE
    ? GoldenGateParameters
    : {};

type ParameterKey<TAssembly extends ASSEMBLY_TYPE> = keyof AssemblyParameters<TAssembly>;

type AssemblyInputParameters<TAssembly extends ASSEMBLY_TYPE> = Record<
  ParameterKey<TAssembly>,
  {number: boolean; nullable: boolean}
>;

interface RenderInputProps<TAssembly extends ASSEMBLY_TYPE> {
  key: ParameterKey<TAssembly>;
  onChange: (e: InputChangeEvent) => void;
}

interface ParametersFormProps<TAssembly extends ASSEMBLY_TYPE> {
  renderInput: (props: RenderInputProps<TAssembly>) => JSX.Element;
  parameterUpdater: (
    key: ParameterKey<TAssembly>,
    cast: (value: string) => number
  ) => (e: InputChangeEvent | SelectChangeEvent) => AssemblyParameters<TAssembly> | null;
  updateParameterByValue: (
    key: ParameterKey<TAssembly>,
    cast: (value: string) => string | number,
    value: string | number,
    options: {
      save?: boolean;
      existingParams?: AssemblyParameters<TAssembly>;
    }
  ) => AssemblyParameters<TAssembly> | null;
  localParameters: AssemblyParameters<TAssembly>;
}

interface FinalizeComponentProps<TAssembly extends ASSEMBLY_TYPE> {
  children?: ReactNode;
  model: Assembly;
  showParameters: boolean;
  updateParameters: (parameters: AssemblyParameters<TAssembly>) => void;
  toggleParameters: () => void;
  createAssembly: () => void;
  deleteAssembly: () => void;
}

interface UpdateParameterProps<
  TAssembly extends ASSEMBLY_TYPE.GOLDEN_GATE | ASSEMBLY_TYPE.GIBSON,
> extends FinalizeComponentProps<TAssembly> {
  parameters: AssemblyInputParameters<TAssembly>;
  renderParametersForm: (parameters: ParametersFormProps<TAssembly>) => JSX.Element;
}

interface UpdateParameterState<TAssembly extends ASSEMBLY_TYPE.GOLDEN_GATE | ASSEMBLY_TYPE.GIBSON> {
  localParameters: AssemblyParameters<TAssembly>;
}

class UpdateParameterComponent<
  TAssembly extends ASSEMBLY_TYPE.GOLDEN_GATE | ASSEMBLY_TYPE.GIBSON,
> extends React.Component<UpdateParameterProps<TAssembly>, UpdateParameterState<TAssembly>> {
  // Because we need to support invalid state (i.e. while typing out a number), we copy the parameters to
  // localParameters (fields are rendered off localParameters) and selectively update server parameters.
  override state: UpdateParameterState<TAssembly> = {
    localParameters: this.props.model.parameters() as AssemblyParameters<TAssembly>,
  };

  // Override local parameters with model parameters always.
  override UNSAFE_componentWillReceiveProps(nextProps: UpdateParameterProps<TAssembly>): void {
    this.setState({
      localParameters: nextProps.model.parameters() as AssemblyParameters<TAssembly>,
    });
  }

  renderParameters(): JSX.Element | null {
    if (!this.props.showParameters) {
      return null;
    }
    return (
      <div className="assembly-settings">
        {this.props.renderParametersForm({
          renderInput: this.renderInput,
          parameterUpdater: this.parameterUpdater,
          updateParameterByValue: this.updateParameterByValue,
          localParameters: this.state.localParameters,
        })}
      </div>
    );
  }

  renderInput = ({key, onChange}: RenderInputProps<TAssembly>): JSX.Element => {
    const value = this.state.localParameters[key];
    const valid = this.serverValid([key, value]);
    return (
      <span className={classNames({'has-error': !valid})}>
        <input type="text" onChange={onChange} value={value as string} className="form-control" />
      </span>
    );
  };

  serverValid = ([key, value]: [
    ParameterKey<TAssembly>,
    AssemblyParameters<TAssembly>[ParameterKey<TAssembly>],
  ]): boolean => {
    const {number, nullable} = this.props.parameters[key];
    if (value == null) {
      return nullable;
    }
    if (number) {
      return value.toString().length > 0;
    }
    return true;
  };

  parameterUpdater =
    (key: ParameterKey<TAssembly>, cast: (value: string) => number) =>
    (e: InputChangeEvent | SelectChangeEvent) =>
      this.updateParameterByValue(key, cast, e.target.value);

  // Returns new parameters if parameter was valid, null otherwise.
  updateParameterByValue = (
    key: ParameterKey<TAssembly>,
    cast: (value: string) => number,
    value: string,
    options: {
      save?: boolean;
      existingParams?: AssemblyParameters<TAssembly>;
    } = {}
  ): AssemblyParameters<TAssembly> | null => {
    const {save = true, existingParams = this.state.localParameters} = options;

    // @ts-expect-error We use Number() for stricter casting, so '1abc' doesn't cast to '1'
    if (value.length && (cast === parseInt || cast === parseFloat) && Number.isNaN(cast(Number(value)))) {
      return null;
    }
    // Need to remove trailing '.' for ints
    if (value.length && cast === parseInt) {
      // @ts-expect-error We use Number() for stricter casting, so '1abc' doesn't cast to '1'
      value = cast(Number(value));
    }
    // @ts-expect-error _.object is not typesafe
    const newParams = reactUpdate(existingParams, {$merge: {[key]: value}});
    this.setState({localParameters: newParams});
    if (save && _.all(_.pairs(newParams), this.serverValid)) {
      this.props.updateParameters(newParams);
    }
    return newParams;
  };

  override render = (): JSX.Element => (
    <FinalizeWrapper<TAssembly> {...this.props}>{this.renderParameters()}</FinalizeWrapper>
  );
}

class FinalizeWrapper<TAssembly extends ASSEMBLY_TYPE> extends React.Component<
  FinalizeComponentProps<TAssembly>
> {
  handleClickAssemble = (): void => {
    analytics.track('assembly', 'assemble_click', {
      product: ANALYTICS_MODULE.MOLBIO,
      assemblyMethod: this.props.model.method(),
    });
    this.props.createAssembly();
  };

  setName = (e: InputChangeEvent): void => {
    let name = e.target.value;
    if (name.length > ASSEMBLY_NAME_MAX_LENGTH) {
      name = name.slice(0, ASSEMBLY_NAME_MAX_LENGTH);
      notifications.warn(`Assembly names can't be more than ${ASSEMBLY_NAME_MAX_LENGTH} characters.`, {
        id: 'assembly-name',
      });
    }
    this.props.model.set({name});
  };

  override render(): JSX.Element {
    return (
      <div className="assembly-finalize u-small-spacing-left">
        <div>
          <div className="input-group u-small-spacing-bottom">
            <input
              type="text"
              placeholder="Assembly Name"
              value={this.props.model.name()}
              onChange={this.setName}
              className="u-small-spacing-bottom"
              {...getTestProps({component: 'FinalizeWrapper', element: 'input'})}
            />
            <Button.Group w="300px">
              <Tooltip label="Delete Assembly" withinPortal={true}>
                <Button
                  aria-label="Delete Assembly"
                  onClick={this.props.deleteAssembly}
                  size="sm"
                  variant="tertiary"
                >
                  <BenchlingIcon icon="format-delete" />
                </Button>
              </Tooltip>
              {this.props.toggleParameters != null && (
                <Tooltip label="Enzyme / Primer Settings">
                  <Button
                    aria-label="Enzyme / Primer Settings"
                    aria-pressed={this.props.showParameters}
                    onClick={this.props.toggleParameters}
                    size="sm"
                    variant="tertiary"
                  >
                    <BenchlingIcon icon="settings" />
                  </Button>
                </Tooltip>
              )}
              <Button
                disabled={!this.props.model.canCreate() || this.props.model.name().length === 0}
                onClick={this.handleClickAssemble}
                size="sm"
                variant="primary"
                {...getTestProps({component: 'FinalizeWrapper', element: 'assembleButton'})}
              >
                Assemble
              </Button>
            </Button.Group>
          </div>
        </div>
        {this.props.children}
      </div>
    );
  }
}

export const DigestFinalize = (props: FinalizeComponentProps<ASSEMBLY_TYPE.DIGEST>): JSX.Element => (
  // eslint-disable-next-line no-restricted-syntax
  <FinalizeWrapper<ASSEMBLY_TYPE.DIGEST> {..._.extend({}, props, {toggleParameters: null})} />
);

const DEFAULT_GIBSON_PARAMETERS: AssemblyInputParameters<ASSEMBLY_TYPE.GIBSON> = {
  tmAnneal: {number: true, nullable: false},
  tmWhole: {number: true, nullable: false},
  tmDiff: {number: true, nullable: false},
  minOverlap: {number: true, nullable: false},
  maxOverlap: {number: true, nullable: false},
};

const renderGibsonForm = ({
  renderInput,
  parameterUpdater,
}: ParametersFormProps<ASSEMBLY_TYPE.GIBSON>): JSX.Element => (
  <Row>
    <Col sm={6}>
      <Row>
        <Col sm={8}>
          Min T<sub>m</sub>
          {" for primer's binding bases"}
        </Col>
        <Col sm={4}>
          {renderInput({
            key: 'tmAnneal',
            onChange: parameterUpdater('tmAnneal', parseFloat),
          })}
          {' \u00B0C'}
        </Col>
      </Row>
      <Row>
        <Col sm={8}>
          Min T<sub>m</sub>
          {' for whole primer'}
        </Col>
        <Col sm={4}>
          {renderInput({
            key: 'tmWhole',
            onChange: parameterUpdater('tmWhole', parseFloat),
          })}
          {' \u00B0C'}
        </Col>
      </Row>
    </Col>
    <Col sm={6}>
      <Row>
        <Col sm={8}>
          Max T<sub>m</sub>
          {' diff for primer pairs'}
        </Col>
        <Col sm={4}>
          {renderInput({
            key: 'tmDiff',
            onChange: parameterUpdater('tmDiff', parseFloat),
          })}
          {' \u00B0C'}
        </Col>
      </Row>
      <Row>
        <Col sm={8}>Length of homology/binding regions</Col>
        <Col sm={4}>
          {renderInput({
            key: 'minOverlap',
            onChange: parameterUpdater('minOverlap', parseInt),
          })}
          {' - '}
          {renderInput({
            key: 'maxOverlap',
            onChange: parameterUpdater('maxOverlap', parseInt),
          })}
          {' bp'}
        </Col>
      </Row>
    </Col>
  </Row>
);

export const GibsonFinalize = (props: FinalizeComponentProps<ASSEMBLY_TYPE.GIBSON>): JSX.Element => (
  <UpdateParameterComponent<ASSEMBLY_TYPE.GIBSON>
    {...props}
    parameters={DEFAULT_GIBSON_PARAMETERS}
    renderParametersForm={renderGibsonForm}
  />
);

const DEFAULT_GOLDEN_GATE_PARAMETERS: AssemblyInputParameters<ASSEMBLY_TYPE.GOLDEN_GATE> = {
  preRecognitionSiteLength: {number: true, nullable: false},
  preRecognitionSiteBases: {number: false, nullable: true},
  bindingRegionMinTM: {number: true, nullable: false},
  bindingRegionMinLength: {number: true, nullable: false},
  primerPairMaxTMDiff: {number: true, nullable: false},
  enzymeID: {number: true, nullable: false},
};

export function GoldenGateFinalize(props: FinalizeComponentProps<ASSEMBLY_TYPE.GOLDEN_GATE>): JSX.Element {
  const renderForm = ({
    renderInput,
    parameterUpdater,
    updateParameterByValue,
    localParameters,
  }: ParametersFormProps<ASSEMBLY_TYPE.GOLDEN_GATE>): JSX.Element => {
    // Clear out preRecognitionSiteBases when length changes.
    const updatePreRecogLength = (e: InputChangeEvent): void => {
      const newParams = updateParameterByValue('preRecognitionSiteLength', parseInt, e.target.value, {
        save: false,
      });
      const bases = (props.model.parameters() as GoldenGateParameters).preRecognitionSiteBases;
      if (
        !newParams ||
        e.target.value.length === 0 ||
        newParams.preRecognitionSiteLength === (bases != null ? bases.length : undefined)
      ) {
        return;
      }
      // eslint-disable-next-line @typescript-eslint/unbound-method
      updateParameterByValue('preRecognitionSiteBases', _.identity, '', {existingParams: newParams});
    };

    // Update preRecognitionSiteLength when bases change.
    const updatePreRecogBases = (e: InputChangeEvent): void => {
      const bases = util.sanitize(e.target.value, util.NucleotideType.DNA).toUpperCase();
      // eslint-disable-next-line @typescript-eslint/unbound-method
      const newParams = updateParameterByValue('preRecognitionSiteBases', _.identity, bases, {
        save: false,
      });
      if (!newParams || bases.length === 0) {
        return;
      }
      updateParameterByValue('preRecognitionSiteLength', parseInt, bases.length, {
        existingParams: newParams,
      });
    };

    let enzymeID;
    return (
      <Row>
        <Col sm={4}>
          <Row>
            <Col sm={6}>Pre-recognition site length</Col>
            <Col sm={6}>
              {renderInput({
                key: 'preRecognitionSiteLength',
                onChange: updatePreRecogLength,
              })}
              {' bp'}
            </Col>
          </Row>
          <Row>
            {/* space used in between glyphicon */}
            <Col sm={6}>
              {'Pre-recognition site bases '}
              <PopoverIcon
                renderContent={() =>
                  "These bases will be included before the enzyme recognition site in each primer's overhang. If they are not specified, a random string of bases will be used."
                }
                placement={FloatingPlacement.TOP}
                icon="circle-question"
              />
            </Col>
            <Col sm={6}>
              {renderInput({
                key: 'preRecognitionSiteBases',
                onChange: updatePreRecogBases,
              })}
            </Col>
          </Row>
        </Col>
        <Col sm={4}>
          <Row>
            <Col sm={6}>
              Min T<sub>m</sub>
              {' for binding region'}
            </Col>
            <Col sm={6}>
              {renderInput({
                key: 'bindingRegionMinTM',
                onChange: parameterUpdater('bindingRegionMinTM', parseFloat),
              })}
              {' \u00B0C'}
            </Col>
          </Row>
          <Row>
            <Col sm={6}>Min length for binding region</Col>
            <Col sm={6}>
              {renderInput({
                key: 'bindingRegionMinLength',
                onChange: parameterUpdater('bindingRegionMinLength', parseInt),
              })}
              {' bp'}
            </Col>
          </Row>
        </Col>
        <Col sm={4}>
          <Row>
            <Col sm={6}>
              Max T<sub>m</sub>
              {' diff for primer pairs'}
            </Col>
            <Col sm={6}>
              {renderInput({
                key: 'primerPairMaxTMDiff',
                onChange: parameterUpdater('primerPairMaxTMDiff', parseFloat),
              })}
              {' \u00B0C'}
            </Col>
          </Row>
          <Row>
            <Col sm={6}>Type IIS Enzyme</Col>
            <Col sm={6}>
              <select onChange={parameterUpdater('enzymeID', parseInt)} value={localParameters.enzymeID}>
                {(() => {
                  const result = [];
                  for (enzymeID of Array.from(TYPE_IIS_ENZYME_IDS)) {
                    result.push(
                      <option value={enzymeID} key={`enzyme-choice-${enzymeID}`}>
                        {COMMON_ENZYMES.get(enzymeID).get('name')}
                      </option>
                    );
                  }
                  return result;
                })()}
              </select>
            </Col>
          </Row>
        </Col>
      </Row>
    );
  };

  return (
    <UpdateParameterComponent<ASSEMBLY_TYPE.GOLDEN_GATE>
      {...props}
      parameters={DEFAULT_GOLDEN_GATE_PARAMETERS}
      renderParametersForm={renderForm}
    />
  );
}
